/**
 * Cart Service
 *
 * Service for handling WooCommerce cart operations via REST API
 *
 * @package WooAIChatbot
 * @author Roman Siromskyi <leowebmark@gmail.com>
 */
/**
 * Get API configuration from WordPress globals
 */
const getApiConfig = () => {
    const globals = window.wooAiChatbot ?? window.wooAIChatbot;
    // Use hardcoded /wp-json to avoid string replacement issues
    return {
        restUrl: '/wp-json',
        nonce: globals?.nonce || '',
    };
};
/**
 * Generic fetch wrapper for API calls
 */
const fetchAPI = async (endpoint, options = {}) => {
    const { restUrl, nonce } = getApiConfig();
    const url = `${restUrl}${endpoint}`;
    try {
        const response = await fetch(url, {
            ...options,
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': nonce,
                ...options.headers,
            },
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({
                message: `HTTP error! status: ${response.status}`,
                code: response.status.toString(),
            }));
            throw new Error(error.message || 'Request failed');
        }
        return response.json();
    }
    catch (error) {
        if (error instanceof Error) {
            throw error;
        }
        throw new Error('An unexpected error occurred');
    }
};
/**
 * Cart Service Class
 *
 * Handles all cart-related API operations
 */
class CartService {
    constructor() {
        Object.defineProperty(this, "baseEndpoint", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: '/woo-ai/v1/cart'
        });
    }
    /**
     * Add product to cart
     *
     * @param params - Add to cart parameters
     * @returns Promise with add to cart response
     *
     * @example
     * ```typescript
     * // Add simple product
     * const result = await cartService.addToCart({
     *   product_id: 123,
     *   quantity: 2
     * });
     *
     * // Add variable product
     * const result = await cartService.addToCart({
     *   product_id: 456,
     *   variation_id: 457,
     *   variation: {
     *     'attribute_pa_color': 'blue',
     *     'attribute_pa_size': 'large'
     *   },
     *   quantity: 1
     * });
     * ```
     */
    async addToCart(params) {
        if (!params.product_id || params.product_id <= 0) {
            throw new Error('Valid product ID is required');
        }
        try {
            const response = await fetchAPI(`${this.baseEndpoint}/add`, {
                method: 'POST',
                body: JSON.stringify({
                    product_id: params.product_id,
                    quantity: params.quantity || 1,
                    variation_id: params.variation_id || 0,
                    variation: params.variation || {},
                }),
            });
            return response;
        }
        catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to add to cart: ${error.message}`);
            }
            throw new Error('Failed to add to cart: Unknown error');
        }
    }
    /**
     * Get cart contents
     *
     * Retrieves current cart state with items and totals.
     *
     * @returns Promise with cart data
     *
     * @example
     * ```typescript
     * const cart = await cartService.getCart();
     * // cart.item_count - number of items
     * // cart.totals.total - total price
     * ```
     */
    async getCart() {
        try {
            const response = await fetchAPI(this.baseEndpoint, {
                method: 'GET',
            });
            return response.cart;
        }
        catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to get cart: ${error.message}`);
            }
            throw new Error('Failed to get cart: Unknown error');
        }
    }
    /**
     * Clear entire cart
     *
     * Removes all items from the cart.
     *
     * @returns Promise that resolves when cart is cleared
     *
     * @example
     * ```typescript
     * await cartService.clearCart();
     * // Cart is now empty
     * ```
     */
    async clearCart() {
        try {
            await fetchAPI(this.baseEndpoint, {
                method: 'DELETE',
            });
        }
        catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to clear cart: ${error.message}`);
            }
            throw new Error('Failed to clear cart: Unknown error');
        }
    }
    /**
     * Remove item from cart
     *
     * Removes a specific cart item by its cart key.
     *
     * @param cartItemKey - Cart item key to remove
     * @returns Promise with updated cart data
     *
     * @example
     * ```typescript
     * const cart = await cartService.removeItem('abc123def456');
     * // cart.totals.total - new cart total
     * ```
     */
    async removeItem(cartItemKey) {
        if (!cartItemKey || typeof cartItemKey !== 'string') {
            throw new Error('Valid cart item key is required');
        }
        try {
            const response = await fetchAPI(`${this.baseEndpoint}/item/${encodeURIComponent(cartItemKey)}`, {
                method: 'DELETE',
            });
            return response.cart;
        }
        catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to remove item: ${error.message}`);
            }
            throw new Error('Failed to remove item: Unknown error');
        }
    }
    /**
     * Update cart item quantity
     *
     * Changes the quantity of a specific cart item.
     * Setting quantity to 0 removes the item.
     *
     * @param cartItemKey - Cart item key
     * @param quantity - New quantity (0 to remove)
     * @returns Promise with updated cart data
     *
     * @example
     * ```typescript
     * // Update quantity
     * const cart = await cartService.updateQuantity('abc123def456', 3);
     *
     * // Remove by setting to 0
     * const cart = await cartService.updateQuantity('abc123def456', 0);
     * ```
     */
    async updateQuantity(cartItemKey, quantity) {
        if (!cartItemKey || typeof cartItemKey !== 'string') {
            throw new Error('Valid cart item key is required');
        }
        if (quantity < 0) {
            throw new Error('Quantity must be 0 or greater');
        }
        try {
            const response = await fetchAPI(`${this.baseEndpoint}/item/${encodeURIComponent(cartItemKey)}`, {
                method: 'PUT',
                body: JSON.stringify({ quantity }),
            });
            return response.cart;
        }
        catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to update quantity: ${error.message}`);
            }
            throw new Error('Failed to update quantity: Unknown error');
        }
    }
    /**
     * Apply coupon to cart
     *
     * Applies a discount coupon code to the cart.
     *
     * @param couponCode - Coupon code to apply
     * @returns Promise with updated cart data
     *
     * @example
     * ```typescript
     * try {
     *   const cart = await cartService.applyCoupon('SAVE20');
     *   // cart.totals.discount - applied discount
     * } catch (error) {
     *   // Handle invalid coupon
     * }
     * ```
     */
    async applyCoupon(couponCode) {
        if (!couponCode || typeof couponCode !== 'string') {
            throw new Error('Valid coupon code is required');
        }
        const trimmedCode = couponCode.trim();
        if (trimmedCode.length === 0) {
            throw new Error('Coupon code cannot be empty');
        }
        try {
            const response = await fetchAPI(`${this.baseEndpoint}/coupon`, {
                method: 'POST',
                body: JSON.stringify({ coupon_code: trimmedCode }),
            });
            return response.cart;
        }
        catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to apply coupon: ${error.message}`);
            }
            throw new Error('Failed to apply coupon: Unknown error');
        }
    }
    /**
     * Get checkout URL
     *
     * Returns the WooCommerce checkout page URL.
     * Optionally includes query parameters for pre-filled fields.
     *
     * @param params - Optional URL parameters
     * @returns Promise with checkout URL
     *
     * @example
     * ```typescript
     * // Get basic checkout URL
     * const url = await cartService.getCheckoutUrl();
     * window.location.href = url;
     *
     * // Get checkout URL with pre-filled email
     * const url = await cartService.getCheckoutUrl({
     *   billing_email: 'user@example.com'
     * });
     * ```
     */
    async getCheckoutUrl(params) {
        try {
            const response = await fetchAPI(`${this.baseEndpoint}/checkout`, {
                method: 'POST',
                body: JSON.stringify({ params: params || {} }),
            });
            return response.checkout_url;
        }
        catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to get checkout URL: ${error.message}`);
            }
            throw new Error('Failed to get checkout URL: Unknown error');
        }
    }
    /**
     * Navigate to checkout
     *
     * Helper method to redirect user to checkout page.
     *
     * @param params - Optional URL parameters
     * @returns Promise that resolves when navigation starts
     *
     * @example
     * ```typescript
     * // Navigate to checkout
     * await cartService.checkout();
     *
     * // Navigate with pre-filled data
     * await cartService.checkout({
     *   billing_email: 'user@example.com',
     *   billing_phone: '+972501234567'
     * });
     * ```
     */
    async checkout(params) {
        const checkoutUrl = await this.getCheckoutUrl(params);
        window.location.href = checkoutUrl;
    }
}
// Export singleton instance
export default new CartService();
